import Image from "next/image";
import { useEffect } from "react";

import type { Account } from "@/graphql/codegen/graphql";
import { useImageUpload } from "@/hooks/use-image-upload";
import { DEFAULT_PROFILE_PIC } from "@/utils/constants";
import { getUserProfilePicture } from "@/utils/helpers";
import { cn } from "@/utils/lib";

import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface UploadImageProps {
  user: Account | null;
  onImageSelected: (fileName: string, image: string) => void;
  className?: string;
  imgSize?: number;
  defaultImage?: string;
}

const UploadImage = ({
  user,
  onImageSelected,
  className = "",
  imgSize = 60,
  defaultImage = DEFAULT_PROFILE_PIC,
}: UploadImageProps) => {
  const { image, setImage, handleImageChange } =
    useImageUpload(onImageSelected);

  useEffect(() => {
    if (user) {
      setImage(getUserProfilePicture(user));
    }
  }, [setImage, user, user?.publicKey]);

  return (
    <div className={cn(className, "flex w-full items-center justify-center")}>
      <Label
        htmlFor="profile-pic"
        className="inline-flex rounded-full border text-center"
        style={{ width: `${imgSize}px` }}
      >
        {image ? (
          <div
            className="
              group relative cursor-pointer overflow-hidden rounded-full
            "
            style={{ width: `${imgSize}px`, height: `${imgSize}px` }}
          >
            <Image
              src={image}
              className="mx-auto cursor-pointer rounded-full object-cover"
              alt="Profile Picture"
              width={imgSize}
              height={imgSize}
              style={{ width: `${imgSize}px`, height: `${imgSize}px` }}
              onError={() => setImage(null)}
            />

            <span
              className="
                absolute bottom-0 w-full -translate-x-1/2 bg-black py-1 text-xs
                text-white transition-opacity
                group-hover:opacity-100
                md:opacity-0
              "
            >
              Upload
            </span>
          </div>
        ) : (
          <div
            className="
              group relative cursor-pointer overflow-hidden rounded-full
            "
          >
            <Image
              className="
                size-14 cursor-pointer rounded-full border border-border
                bg-accent
              "
              width={imgSize}
              height={imgSize}
              style={{ width: `${imgSize}px`, height: `${imgSize}px` }}
              alt="Default Profile Picture"
              src={defaultImage}
            />

            <span
              className="
                absolute bottom-0 w-full -translate-x-1/2 bg-black py-1 text-xs
                text-white transition-opacity
                group-hover:opacity-100
                md:opacity-0
              "
            >
              Upload
            </span>
          </div>
        )}
      </Label>

      <Input
        id="profile-pic"
        type="file"
        className="hidden"
        accept=".png, .jpg, .jpeg, .webp"
        onChange={handleImageChange}
      />
    </div>
  );
};

export default UploadImage;
